import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import LabValues from "./LabValues";
import InitContext from "../pages/InitContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine, faTrash } from "@fortawesome/free-solid-svg-icons";
import useMmcService from "../../services/MmcService";
import ErrorMessage from "../errorMessage/ErrorMessage";
import Spinner from "../spinner/Spinner";
import LabTestsLineChart from "./LabTestsLineChart";
import {formatDateTime, sortData} from "../pages/utils";
import ReactDOM from "react-dom";
import {Consts} from "../pages/consts";
import SortAndAddNewTest from "./SortAndAddNewTest";

const LabTestsTable = ({
                         children, selectedPatient, hcpView, printView = false,
                         chartActionInToolbar = false
                       }) => {
  const { t } = useTranslation();
  const { loading, error, clearError, getDbLabTests, deleteDbLabTest} = useMmcService();
  const [labTests, setLabTests] = useState([]);
  const [addLabTest, setAddLabTest] = useState(false);
  const context = useContext(InitContext);
  const [showLabTestsLineChart, setShowLabTestsLineChart] = useState(false);

  const [sortConfig, setSortConfig] = useState({
    key: Consts.TEST_DATE,
    ascending: false,
  });

  const [sortedLabTests, setSortedLabTests] = useState(
    labTests ? [...labTests] : [],
  );

  useEffect(() => {
    const sorted = sortData(sortConfig?.key, labTests, sortConfig?.ascending);
    setSortedLabTests(sorted);
  }, [labTests, sortConfig]);

  const handleSortChange = (key) => {
    const ascending = sortConfig?.key === key ? !sortConfig?.ascending : true;
    setSortConfig({ key, ascending });
  };

  const patientId = !selectedPatient ? context?.patient?.id : selectedPatient?.id;

  useEffect(() => {
    clearError();

    if (patientId) {
      getDbLabTests(context?.token, patientId).then((data) => {
        handleLabTestsFromDb(data);
      });
    }
  }, [printView, patientId]);

  const handleLabTestsFromDb = (data) => {
    const newArr = data?.filter((test) => test?.labParams?.length > 0);
    setLabTests(newArr);
  };

  const handleCloseLabTestsLineChart = () => {
    setShowLabTestsLineChart(false);
  };

  let message =
    error && error?.includes(Consts.DELETE)
      ? t("error_by_deleting_a_lab_test")
      : t("error_by_retrieving_lab_tests");

  const errorMessage = error && <ErrorMessage message={message} />;
  const spinner = !printView && loading && <Spinner />;

  const handleDeleteLabTest = (testToDelete) => {
    clearError();
    if (testToDelete) {
      deleteDbLabTest(context?.token, testToDelete?.id).then((deletedTest) =>
        handleDeleteResult(deletedTest),
      );
    }
  };

  const handleDeleteResult = (deletedTest) => {
    if (deletedTest) {
      setLabTests(
        labTests?.filter((test) => {
          if (!test?.id || (test?.id && test?.id !== deletedTest?.id)) {
            return test;
          }
        }),
      );
    }
  };

  const handleSaveNewLabTest = (labTest) => {
    if (labTest) {
      labTest = {
        ...labTest,
        testDate: formatDateTime(labTest?.testDate, true),
      };

      setLabTests([...labTests, labTest]);
    }

    setAddLabTest(false);
  };

  const buildLabParamsView = (params, testName) => {
    return params?.map((param, k) => {
      const parts = param?.parameterName.split("&&");

      return (
        <div key={k}>
          {testName === "plasma_concentration_test" ? (
            <>
              <label className="bold-label">
                {parts[0].split("##")[0]}:{" "}
              </label>
              <label className="label-margin-left">
                {param?.value} {param?.unit}
              </label>
              <label className="label-margin-left">
                {(parts[1] !== 'undefined') && `(${t("hours_before_test")}: ${parts[1]} ${t("hours_low")})`}
              </label>
            </>
          ) : (
            <>
              <label className="bold-label">
                {t(parts[0])}:{" "}
              </label>
              <label className="label-margin-left">
                {param?.value}{" "}
                {param?.unit}
              </label>
            </>
          )}
        </div>
      );
    })
  }

  return (
    <>
      {addLabTest &&
        ReactDOM.createPortal(
          <LabValues
            selectedPatient={selectedPatient}
            onSaveNewLabTest={handleSaveNewLabTest}
            showDialog={addLabTest}
            hcpView={hcpView}
          />,
          document.getElementById(Consts.ROOT),
        )
      }
      {showLabTestsLineChart &&
        ReactDOM.createPortal(
          <LabTestsLineChart
            labTests={labTests}
            onCloseDialog={handleCloseLabTestsLineChart}
            showDialog={showLabTestsLineChart}
            patientId={patientId}
          />,
          document.getElementById(Consts.ROOT),
        )
      }
      {!printView && (
        <>
          {(children || !chartActionInToolbar) && (
            <header className="w3-container w3-center w3-padding-small">
              <div className="w3-display-container w3-padding-small">
                {children}
                {!chartActionInToolbar && labTests?.length > 0 && (
                  <div className="w3-display-right">
                    <button
                      type="button"
                      className="symptom-ui-button symptom-ui-button--icon symptom-ui-button--secondary"
                      onClick={() => setShowLabTestsLineChart(true)}
                      aria-label={t("show_chart", {defaultValue: "Show chart"})}
                    >
                      <FontAwesomeIcon icon={faChartLine}/>
                    </button>
                  </div>
                )}
              </div>
            </header>
          )}
          {errorMessage}
          {spinner}
          <SortAndAddNewTest
            labTests={sortedLabTests}
            handleSortChange={handleSortChange}
            setAddLabTest={setAddLabTest}
            sortConfig={sortConfig}
            chartActionInToolbar={chartActionInToolbar}
            onShowLabTestsLineChart={() => setShowLabTestsLineChart(true)}
          />
        </>
      )}
      <div className={printView ? "w3-container w3-left-align" : "w3-container form-content-scroll w3-left-align"}>
        {sortedLabTests?.length > 0 ? (
          <div className={printView ? "w3-container" : "w3-container symptom-cards-surface"}>
            {sortedLabTests?.map((item, i) => {
              return (
                <div
                  className="w3-column w3-card w3-margin symptom-table-card"
                  key={`paramItem${i}`}
                >
                  <div className="w3-row">
                    <div className="w3-col s9 w3-mobile">
                      <label>{item?.testDate}: {t(item?.labTestName)}</label>
                      {buildLabParamsView(item?.labParams, item?.labTestName)}
                    </div>
                    {(context?.role === Consts.HCP_PRESCRIBER || context?.role === Consts.PATIENT) && !printView && (
                      <div className="w3-col s3 w3-mobile">
                        <div className="symptom-action-buttons">
                          <button
                            type="button"
                            onClick={() => handleDeleteLabTest(item)}
                            className="symptom-ui-button symptom-ui-button--icon symptom-ui-button--danger"
                            aria-label={t("delete", {defaultValue: "Delete"})}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="w3-container w3-center">
            <label>
              {selectedPatient ? t("not_available") : t("you_have_not_yet_entered_any_lab_test_results")}.
            </label>
          </div>
        )}
      </div>
    </>
  );
};

export default LabTestsTable;
